import React from "react";
import OnboardingScreen, { OnboardingErrorText, OnboardingTitle } from "../OnboardingScreen";
import { AppleLogoIcon, GoogleGIcon, OnboardingMethodButton, PeakOrDivider } from "../OnboardingButtons";

interface CreateAccountStepProps {
  authBusy: boolean;
  authError: string | null;
  legalAccepted: boolean;
  onLegalAcceptedChange: (accepted: boolean) => void;
  onBack: () => void;
  onApple: () => void;
  onGoogle: () => void;
  onEmail: () => void;
  onSignIn: () => void;
}

/** Required agreement from the previous registration flow. Account creation stays disabled until it is checked. */
export const OnboardingLegalCheckbox: React.FC<{
  accepted: boolean;
  onChange: (accepted: boolean) => void;
}> = ({ accepted, onChange }) => (
  <label className="flex items-start gap-3 font-funnel text-[14px] leading-snug text-peak-text-secondary">
    <input
      type="checkbox"
      checked={accepted}
      onChange={(event) => onChange(event.target.checked)}
      className="mt-0.5 h-4 w-4 shrink-0 accent-current"
    />
    <span>
      By creating an account with Peak, I agree to Peak&apos;s{" "}
      <a
        href="/terms-of-service"
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2"
        onClick={(event) => event.stopPropagation()}
      >
        Terms and Conditions
      </a>{" "}
      and{" "}
      <a
        href="/privacy-policy"
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2"
        onClick={(event) => event.stopPropagation()}
      >
        Privacy Policy
      </a>
      .
    </span>
  </label>
);

const CreateAccountStep: React.FC<CreateAccountStepProps> = ({
  authBusy,
  authError,
  legalAccepted,
  onLegalAcceptedChange,
  onBack,
  onApple,
  onGoogle,
  onEmail,
  onSignIn,
}) => {
  const canStart = legalAccepted && !authBusy;
  return (
    <OnboardingScreen onBack={onBack}>
      <div className="flex flex-col gap-7">
        <OnboardingTitle title="Let's begin" subtitle="How would you like to register?" />
        <OnboardingLegalCheckbox accepted={legalAccepted} onChange={onLegalAcceptedChange} />
        <div className="flex flex-col gap-3">
          <OnboardingMethodButton label="Continue with Apple" muted enabled={canStart} leading={<AppleLogoIcon />} onClick={onApple} />
          <OnboardingMethodButton label="Continue with Google" muted enabled={canStart} leading={<GoogleGIcon />} onClick={onGoogle} />
          <PeakOrDivider />
          <OnboardingMethodButton label="Continue with email" primary enabled={canStart} onClick={onEmail} />
          <OnboardingErrorText>{authError}</OnboardingErrorText>
        </div>
        <button type="button" onClick={onSignIn} className="w-full text-center font-funnel text-[16px] text-peak-text">
          I already have an account, <span className="font-semibold">sign in</span>
        </button>
      </div>
    </OnboardingScreen>
  );
};

export default CreateAccountStep;
